

import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { Card } from "@/components/Card/Card";
import {
  IconPaperclip,
  IconDownload,
  IconChevronDown,
  IconChevronRight,
  IconFile,
  IconUpload,
  IconTrash,
  IconLink,
  IconCheck,
  IconLoader2,
  IconAlertCircle,
  IconAlertTriangle,
  IconX,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { SHEET_INITIAL, SHEET_ANIMATE, SHEET_EXIT, SHEET_SPRING } from "@/components/Modal/sheetMotion";
import { useTranslations } from "next-intl";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { Attachment } from "@/types/Attachment";
import {
  getGuestAttachments,
  getDownloadUrl,
  deleteFile,
  generatePortalLink,
  updateAttachment,
} from "@/lib/api/handlers/AttachmentHandling";
import { getDocumentTemplatesHandler } from "@/lib/api/handlers/DocumentTemplateHandling";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { uploadFilePresigned } from "@/lib/utils/attachment-upload";
import { formatFileSize } from "@/lib/utils/email-attachment-validation";
import { deliveredTemplateIds } from "@/lib/utils/attachment-display";
import {
  IconFileText,
  IconWallet,
  IconCalendarEvent,
} from "@tabler/icons-react";
import { PanelEmptyState } from "./PanelEmptyState";

type GuestAttachmentsListProps = {
  guestId: string;
};

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

export const GuestAttachmentsList: React.FC<GuestAttachmentsListProps> = ({
  guestId,
}) => {
  const t = useTranslations("attachments");
  const tErrors = useTranslations("errors");
  // Read-only roles (e.g. Auditor in preview) may view/download/copy-link but
  // not upload or delete files. Copy/download stay; writes gate by capability.
  const { can } = useCapabilities();
  const canWriteAtt = can("attachments:write");
  const canDeleteAtt = can("attachments:delete");
  const params = useParams();
  const eventId = params.eventId as string;
  const queryClient = useQueryClient();

  const attachmentsQueryKey = ["guestAttachments", eventId, guestId] as const;
  const invalidateAttachments = () => {
    void queryClient.invalidateQueries({ queryKey: attachmentsQueryKey });
  };

  const attachmentsQ = useQuery({
    queryKey: attachmentsQueryKey,
    queryFn: () => getGuestAttachments(eventId, guestId),
    enabled: !!eventId && !!guestId,
  });
  const attachments: Attachment[] = Array.isArray(attachmentsQ.data) ? attachmentsQ.data : [];
  const loading = attachmentsQ.isLoading;
  // The handler resolves with `{isServerError}` instead of throwing, so a
  // failed read arrives as SUCCESS with non-array data — and used to render as
  // "no attachments for this guest".
  const loadFailed =
    attachmentsQ.isError ||
    (attachmentsQ.data !== undefined && !Array.isArray(attachmentsQ.data));

  const docTemplatesQ = useQuery({
    queryKey: ["documentTemplates", eventId],
    queryFn: () => getDocumentTemplatesHandler(eventId),
    enabled: !!eventId,
  });
  const templatesKnown = Array.isArray(docTemplatesQ.data);
  const documentTemplates: { id: string; name: string; documentType: string }[] =
    Array.isArray(docTemplatesQ.data)
      ? docTemplatesQ.data.map((dt) => ({
          id: dt.documentTemplateId!,
          name: dt.name,
          documentType: dt.documentType,
        }))
      : [];

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copyLoadingId, setCopyLoadingId] = useState<string | null>(null);
  const [copyErrorId, setCopyErrorId] = useState<string | null>(null);

  // Add Document popup state
  const [addDocAttachmentId, setAddDocAttachmentId] = useState<string | null>(null);
  const [addDocTab, setAddDocTab] = useState<"upload" | "template">("upload");
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [creatingDocAttachment, setCreatingDocAttachment] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDownload = async (attachmentId: string, fileId: string) => {
    const result = await getDownloadUrl(eventId, attachmentId, fileId);
    if (result && !("isServerError" in result)) {
      window.open(result.url, "_blank");
    }
  };

  const handleUploadFiles = async (attachmentId: string, selectedFiles: File[]) => {
    const att = attachments.find((a) => a.attachmentId === attachmentId);
    if (!att) return;

    setUploadErrors([]);
    const errors: string[] = [];
    const maxSizeLabel = `${MAX_FILE_SIZE / (1024 * 1024)} MB`;
    // Oversized files were previously dropped silently — surface each one.
    const valid = selectedFiles.filter((f) => {
      if (f.size > MAX_FILE_SIZE) {
        errors.push(t("fileTooLargeNamed", { name: f.name, size: maxSizeLabel }));
        return false;
      }
      return true;
    });

    setUploading(true);
    let anySucceeded = false;
    for (const file of valid) {
      // For GENERAL attachments, don't send guestId — file is event-wide
      // For INDIVIDUAL attachments, send guestId to associate with this guest
      const uploadGuestId = att.assignmentType === "INDIVIDUAL" ? guestId : undefined;
      const result = await uploadFilePresigned(eventId, attachmentId, file, uploadGuestId);
      if (result.ok) anySucceeded = true;
      else errors.push(t("uploadFailedNamed", { name: file.name }));
    }
    if (anySucceeded) invalidateAttachments();
    setUploading(false);
    setUploadErrors(errors);
    // Keep the upload panel open when something failed so the error is visible.
    if (errors.length === 0) setAddDocAttachmentId(null);
  };

  const handleDeleteFile = async (attachmentId: string, fileId: string) => {
    const result = await deleteFile(eventId, attachmentId, fileId);
    if (result && "isServerError" in result) return;
    invalidateAttachments();
  };

  const handleCopyLink = async (
    id: string,
    attachmentIds?: string[],
  ) => {
    if (copyLoadingId) return;
    setCopyLoadingId(id);
    setCopiedId(null);
    setCopyErrorId(null);

    const result = await generatePortalLink(
      eventId,
      guestId,
      attachmentIds,
    );

    if (result && !("isServerError" in result)) {
      await navigator.clipboard.writeText(result.url);
      setCopyLoadingId(null);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } else {
      setCopyLoadingId(null);
      setCopyErrorId(id);
      setTimeout(() => setCopyErrorId(null), 2000);
    }
  };

  const currentAttachment = addDocAttachmentId
    ? attachments.find((a) => a.attachmentId === addDocAttachmentId)
    : null;
  // Adding a template starts from what the attachment DELIVERS, never the raw
  // stored ids: an UPLOAD row keeps the ids it had before a source switch, and
  // writing them back revived a template the operator had removed.
  const currentTemplateIds = currentAttachment ? deliveredTemplateIds(currentAttachment) : [];
  // The mail sends the files of UPLOAD / MIXED only, so an attachment with
  // uploads must stay MIXED. `files` here is THIS guest's view: a personal
  // attachment's files for other guests are filtered out, so a personal row
  // that already delivers uploads stays MIXED even when this guest has none.
  const currentHasUploads =
    !!currentAttachment &&
    ((currentAttachment.files?.length ?? 0) > 0 ||
      (currentAttachment.assignmentType === "INDIVIDUAL" &&
        currentAttachment.sourceType !== "DOCUMENT_BUILDER"));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <LoadingSpinner />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="p-4 text-center" role="alert">
        <p className="font-error">{tErrors("somethingWentWrong")}</p>
        <button
          type="button"
          className="body-2-bold underline mt-2 disabled:opacity-50"
          disabled={attachmentsQ.isFetching}
          onClick={() => void attachmentsQ.refetch()}
        >
          {tErrors("retry")}
        </button>
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <PanelEmptyState riv="NoAdditionalField" title={t("noGuestAttachments")} />
      </div>
    );
  }

  const linkAttachments = attachments.filter(
    (att) => att.accessType === "LINK",
  );
  const hasLinkAttachments = linkAttachments.length > 0;
  const hasClosedAttachments = attachments.some(
    (att) => att.accessType === "CLOSED",
  );
  const linkAttachmentIds = linkAttachments.map((att) => att.attachmentId);

  return (
    <div className="flex flex-col gap-2 py-3">
      <div className="flex items-center gap-2 px-1">
        <h3 className="body-2-medium">{t("title")}</h3>
        <span className="chip-purple-bg chip-purple-fg body-4-medium px-1.5 py-0.5 rounded-full">
          {attachments.length}
        </span>
        {hasLinkAttachments && (
          <button
            className="ml-auto flex items-center gap-1 font-tertiary hover:font-primary transition-colors disabled:opacity-50"
            disabled={copyLoadingId === "all"}
            onClick={() => handleCopyLink("all", linkAttachmentIds)}
            title={t("copyAllLinks")}
          >
            {copyLoadingId === "all" ? (
              <IconLoader2 size={14} className="animate-spin" />
            ) : copiedId === "all" ? (
              <IconCheck size={14} className="text-green-500" />
            ) : copyErrorId === "all" ? (
              <IconAlertCircle size={14} className="text-red-500" />
            ) : (
              <IconLink size={14} />
            )}
            <span className="body-4-medium">
              {copiedId === "all" ? t("linkCopied") : t("copyAllLinks")}
            </span>
          </button>
        )}
      </div>
      {hasLinkAttachments && hasClosedAttachments && (
        <p className="body-4-regular font-tertiary px-1">
          {t("onlyLinkIncluded")}
        </p>
      )}
      {attachments.map((att) => {
        const isExpanded = expandedIds.has(att.attachmentId);
        const files = att.files ?? [];
        const isLink = att.accessType === "LINK";
        // A template attachment has no stored file for this guest — its
        // documents are rendered per guest (mail, portal tap). Count them and
        // name them, or the row says "0 files / no files yet" about an
        // attachment that does deliver something.
        // Counted from the rows actually listed: an id the event no longer
        // has (deleted / foreign template) renders nothing — the portal drops
        // it too — and must not say "1 file" over an empty panel.
        const deliveredIds = deliveredTemplateIds(att);
        const templateRows = deliveredIds.flatMap((id) => {
          const dt = documentTemplates.find((d) => d.id === id);
          return dt ? [dt] : [];
        });
        // Only a LOADED list can say an id is gone. While it loads, or when it
        // is refused (Documents plugin off, no documents:read — the portal and
        // mail still deliver on the Attachments plugin alone), count every id
        // the attachment delivers instead of "0 / No files yet".
        const documentCount =
          files.length + (templatesKnown ? templateRows.length : deliveredIds.length);

        return (
          <div
            key={att.attachmentId}
            className="rounded-xl border outline-tertiary overflow-hidden"
          >
            {/* Header */}
            <button
              className="flex items-center gap-2 w-full px-3 py-2.5 hover:surface-secondary transition-colors"
              onClick={() => toggleExpand(att.attachmentId)}
            >
              {isExpanded ? (
                <IconChevronDown size={16} className="font-tertiary shrink-0" />
              ) : (
                <IconChevronRight size={16} className="font-tertiary shrink-0" />
              )}
              <IconPaperclip size={16} className="font-quaternary shrink-0" />
              <span className="body-3-medium truncate flex-1 text-left">
                {att.name}
              </span>
              <span
                className={`shrink-0 body-4-medium px-1.5 py-0.5 rounded-full ${
                  att.assignmentType === "GENERAL"
                    ? "chip-blue-bg chip-blue-fg"
                    : "chip-purple-bg chip-purple-fg"
                }`}
              >
                {att.assignmentType === "GENERAL" ? t("general") : t("personal")}
              </span>
              <span
                className="body-4-regular font-tertiary shrink-0"
                data-testid={`guest-attachment-count-${att.attachmentId}`}
              >
                {documentCount} {t("fileCount")}
              </span>
              {isLink && (
                <span
                  className="shrink-0 font-tertiary hover:font-primary transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyLink(att.attachmentId, [att.attachmentId]);
                  }}
                  title={t("copyLink")}
                >
                  {copyLoadingId === att.attachmentId ? (
                    <IconLoader2 size={14} className="animate-spin" />
                  ) : copiedId === att.attachmentId ? (
                    <IconCheck size={14} className="text-green-500" />
                  ) : copyErrorId === att.attachmentId ? (
                    <IconAlertCircle size={14} className="text-red-500" />
                  ) : (
                    <IconLink size={14} />
                  )}
                </span>
              )}
            </button>

            {/* Files list */}
            {isExpanded && (
              <div className="border-t outline-tertiary">
                {documentCount === 0 ? (
                  <p className="px-3 py-2 body-4-regular font-tertiary">
                    {t("noFiles")}
                  </p>
                ) : (
                  files.map((file) => (
                    <div
                      key={file.attachmentFileId}
                      className="flex items-center gap-2 px-3 py-2 hover:surface-secondary group"
                    >
                      <IconFile size={14} className="font-tertiary shrink-0" />
                      <span className="body-4-regular truncate flex-1">
                        {file.fileName}
                      </span>
                      <span className="body-4-regular font-tertiary shrink-0">
                        {formatFileSize(file.fileSize)}
                      </span>
                      <button
                        className="pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 focus-visible:opacity-100 transition-opacity font-tertiary hover:font-primary shrink-0"
                        onClick={() =>
                          handleDownload(att.attachmentId, file.attachmentFileId)
                        }
                        title={t("downloadFile")}
                      >
                        <IconDownload size={14} />
                      </button>
                      {canDeleteAtt && (
                      <button
                        className="pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 focus-visible:opacity-100 transition-opacity font-tertiary hover:font-error shrink-0"
                        onClick={() =>
                          handleDeleteFile(att.attachmentId, file.attachmentFileId)
                        }
                        title={t("deleteFile")}
                      >
                        <IconTrash size={14} />
                      </button>
                      )}
                    </div>
                  ))
                )}

                {/* Templates: rendered per guest on demand, so there is no
                    stored file to download here — the name and the kind say
                    what the guest gets. */}
                {templateRows.map((dt) => {
                  const Icon =
                    dt.documentType === "WALLET"
                      ? IconWallet
                      : dt.documentType === "CALENDAR"
                        ? IconCalendarEvent
                        : IconFileText;
                  return (
                    <div
                      key={dt.id}
                      className="flex items-center gap-2 px-3 py-2"
                      data-testid={`guest-attachment-template-${att.attachmentId}-${dt.id}`}
                    >
                      <Icon size={14} className="font-tertiary shrink-0" />
                      <span className="body-4-regular truncate flex-1">{dt.name}</span>
                      <span className="body-4-regular font-tertiary shrink-0">
                        {dt.documentType === "WALLET"
                          ? t("walletType")
                          : dt.documentType === "CALENDAR"
                            ? t("calendarType")
                            : t("standardType")}
                      </span>
                    </div>
                  );
                })}

                {/* Add File button */}
                {canWriteAtt && (
                <div className="px-3 py-2 border-t outline-tertiary">
                  <button
                    className="flex items-center gap-1.5 font-secondary hover:font-primary transition-colors w-fit"
                    disabled={uploading}
                    onClick={() => {
                      setAddDocTab("upload");
                      setAddDocAttachmentId(att.attachmentId);
                    }}
                  >
                    {uploading && addDocAttachmentId === att.attachmentId ? (
                      <IconLoader2 size={14} className="animate-spin" />
                    ) : (
                      <IconUpload size={14} />
                    )}
                    <span className="body-4-medium">
                      {uploading && addDocAttachmentId === att.attachmentId
                        ? t("uploadingFile")
                        : t("addFile")}
                    </span>
                  </button>
                </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Add Document popup */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {addDocAttachmentId && (
              <motion.div
                className="fixed left-0 top-0 bg-black/50 flex items-end sm:items-center justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none h-dvh w-screen z-[60]"
                onClick={(e) => {
                  if (e.target === e.currentTarget) setAddDocAttachmentId(null);
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  className="w-full sm:w-auto flex justify-center"
                  initial={SHEET_INITIAL}
                  animate={SHEET_ANIMATE}
                  exit={SHEET_EXIT}
                  transition={SHEET_SPRING}
                >
                  <Card
                    padding="p-6"
                    rounded="rounded-t-3xl sm:rounded-3xl"
                    width="w-full sm:w-[95vw] sm:max-w-110"
                    justify=""
                    customHeader={
                      <h2 className="h2">{t("addDocument")}</h2>
                    }
                    icon={IconX}
                    iconActionOnClick={() => setAddDocAttachmentId(null)}
                  >
                    <div className="flex flex-col gap-4 pt-4">
                      {/* GENERAL attachment warning */}
                      {currentAttachment?.assignmentType === "GENERAL" && (
                        <div className="flex items-start gap-2 p-3 rounded-lg hint-orange-bg border hint-orange-outline">
                          <IconAlertTriangle size={16} className="hint-orange-font shrink-0 mt-0.5" />
                          <p className="body-3-regular hint-orange-font">
                            {t("generalWarning")}
                          </p>
                        </div>
                      )}

                      {/* Tab toggle */}
                      <div className="flex gap-1 p-1 rounded-lg surface-secondary">
                        <button
                          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md body-3-medium transition-colors ${
                            addDocTab === "upload"
                              ? "surface-primary font-primary shadow-sm"
                              : "font-tertiary hover:font-secondary"
                          }`}
                          onClick={() => setAddDocTab("upload")}
                        >
                          <IconUpload size={14} />
                          {t("upload")}
                        </button>
                        <button
                          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md body-3-medium transition-colors ${
                            addDocTab === "template"
                              ? "surface-primary font-primary shadow-sm"
                              : "font-tertiary hover:font-secondary"
                          }`}
                          onClick={() => setAddDocTab("template")}
                        >
                          <IconFile size={14} />
                          {t("documentBuilder")}
                        </button>
                      </div>

                      {/* Upload tab content */}
                      {addDocTab === "upload" && (
                        <motion.div
                          className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center gap-3 cursor-pointer transition-colors ${
                            isDragOver
                              ? "outline-primary surface-secondary"
                              : "outline-tertiary hover:surface-tertiary"
                          }`}
                          animate={{ scale: isDragOver ? 1.01 : 1 }}
                          transition={{ duration: 0.15 }}
                          onDrop={(e) => {
                            e.preventDefault();
                            setIsDragOver(false);
                            handleUploadFiles(
                              addDocAttachmentId,
                              Array.from(e.dataTransfer.files),
                            );
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragOver(true);
                          }}
                          onDragLeave={() => setIsDragOver(false)}
                          onClick={() =>
                            document
                              .getElementById("guest-attachment-file-input")
                              ?.click()
                          }
                        >
                          {uploading ? (
                            <IconLoader2
                              size={24}
                              className="font-tertiary animate-spin"
                            />
                          ) : (
                            <>
                              <div className="w-12 h-12 rounded-xl border surface-secondary flex items-center justify-center">
                                <IconUpload
                                  size={24}
                                  className="font-tertiary"
                                />
                              </div>
                              <p className="body-3-regular font-secondary text-center">
                                {t("dragDropFiles")}{" "}
                                <span className="underline font-primary">
                                  {t("browse")}
                                </span>
                              </p>
                              <p className="body-4-regular font-tertiary">
                                {t("supportedFormats")} &middot; {t("maxFileSize")}
                              </p>
                            </>
                          )}
                          <input
                            id="guest-attachment-file-input"
                            type="file"
                            multiple
                            className="hidden"
                            accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx,.csv,.zip"
                            onChange={(e) => {
                              if (!e.target.files) return;
                              handleUploadFiles(
                                addDocAttachmentId,
                                Array.from(e.target.files),
                              );
                              e.target.value = "";
                            }}
                          />
                        </motion.div>
                      )}
                      {addDocTab === "upload" && uploadErrors.length > 0 && (
                        <div className="space-y-0.5 mt-2">
                          {uploadErrors.map((msg, i) => (
                            <p key={i} className="body-4-regular text-red-500 text-center">
                              {msg}
                            </p>
                          ))}
                        </div>
                      )}

                      {/* Document Builder tab content */}
                      {addDocTab === "template" && (
                        <>
                          {documentTemplates.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 gap-2">
                              <IconFileText size={32} className="font-quaternary" />
                              <p className="body-3-regular font-tertiary text-center">
                                {t("noTemplatesFound")}
                              </p>
                            </div>
                          ) : creatingDocAttachment ? (
                            <div className="flex items-center justify-center py-8">
                              <IconLoader2 size={24} className="animate-spin font-tertiary" />
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {documentTemplates.filter((dt) => !currentTemplateIds.includes(dt.id)).map((dt) => {
                                const Icon =
                                  dt.documentType === "WALLET"
                                    ? IconWallet
                                    : dt.documentType === "CALENDAR"
                                      ? IconCalendarEvent
                                      : IconFileText;
                                return (
                                  <button
                                    key={dt.id}
                                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:surface-secondary transition-colors text-left w-full"
                                    onClick={async () => {
                                      setCreatingDocAttachment(true);
                                      setDocError(null);
                                      try {
                                        const result = await updateAttachment(eventId, addDocAttachmentId, {
                                          documentBuilderTemplateIds: [...currentTemplateIds, dt.id],
                                          sourceType: currentHasUploads ? "MIXED" : "DOCUMENT_BUILDER",
                                        });
                                        if (result && !("isServerError" in result)) {
                                          invalidateAttachments();
                                          setAddDocAttachmentId(null);
                                        } else {
                                          setDocError(t("docBuilderError"));
                                        }
                                      } catch (error) {
                                        console.error("[GuestAttachments] updateAttachment threw:", error);
                                        setDocError(t("docBuilderError"));
                                      } finally {
                                        setCreatingDocAttachment(false);
                                      }
                                    }}
                                  >
                                    <Icon size={18} className="font-tertiary shrink-0" />
                                    <span className="body-3-medium flex-1 min-w-0 truncate">
                                      {dt.name}
                                    </span>
                                    <span className="body-4-regular font-tertiary shrink-0">
                                      {dt.documentType === "WALLET"
                                        ? t("walletType")
                                        : dt.documentType === "CALENDAR"
                                          ? t("calendarType")
                                          : t("standardType")}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {docError && (
                            <p className="body-4-regular text-red-500 text-center py-1">{docError}</p>
                          )}
                        </>
                      )}
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
};
